// WeatherWidget.js - Clean implementation
import React, { useEffect, useCallback } from 'react';
import Card from './Card';
import Loading from './Loading';
import Alert from './Alert';
import { useWeather } from '../context/WeatherContext';
import { formatNumber, formatTemperature } from '../utils/helpers';

const WeatherWidget = () => {
  const { state, loadWeatherData: fetchWeatherData } = useWeather();
  const { currentWeather, forecast, loading, error } = state;

  // Wrap loadWeatherData in useCallback to prevent unnecessary re-renders
  const loadWeatherData = useCallback(() => {
    fetchWeatherData();
  }, [fetchWeatherData]);

  useEffect(() => {
    loadWeatherData();
    // Refresh weather data every 30 minutes
    const intervalId = setInterval(() => {
      loadWeatherData();
    }, 30 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [loadWeatherData]);

  // Helper function to format the day name
  const formatDay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Helper function to get weather icon based on condition
  const getWeatherIcon = (condition) => {
    if (!condition) return '🌤️';
    
    const conditionLower = condition.toLowerCase();
    if (conditionLower.includes('sun') || conditionLower.includes('clear')) {
      return '☀️';
    } else if (conditionLower.includes('cloud')) {
      return '☁️';
    } else if (conditionLower.includes('rain') || conditionLower.includes('drizzle')) {
      return '🌧️';
    } else if (conditionLower.includes('snow')) {
      return '❄️';
    } else if (conditionLower.includes('thunder') || conditionLower.includes('storm')) {
      return '⛈️';
    } else if (conditionLower.includes('fog') || conditionLower.includes('mist')) {
      return '🌫️';
    } else if (conditionLower.includes('wind')) {
      return '💨';
    } else {
      return '🌤️';
    }
  };

  if (loading) {
    return (
      <Card title="Weather Forecast">
        <Loading message="Loading weather data..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Weather Forecast">
        <Alert type="error" message={`Failed to load weather data: ${error}`} />
      </Card>
    );
  }
  
  return (
    <Card title="Weather Forecast">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Current Weather */}
        {currentWeather && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '3rem', marginRight: '1rem', color: '#3b82f6' }}>
              {getWeatherIcon(currentWeather.condition)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                {formatTemperature(currentWeather.temperature)}
              </div>
              <div style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>
                {currentWeather.condition}
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: '#64748b' }}>
                  <span style={{ fontWeight: 500, color: '#94a3b8' }}>Humidity:</span>
                  {formatNumber(currentWeather.humidity, 0, '%')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: '#64748b' }}>
                  <span style={{ fontWeight: 500, color: '#94a3b8' }}>Wind:</span>
                  {formatNumber(currentWeather.windSpeed, 0, 'km/h')} {currentWeather.windDirection}
                </div>
                {currentWeather.precipitation > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: '#64748b' }}>
                    <span style={{ fontWeight: 500, color: '#94a3b8' }}>Precip:</span>
                    {formatNumber(currentWeather.precipitation, 1, 'mm')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Forecast */}
        {forecast && forecast.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem' }}>
            {forecast.map((day, index) => (
              <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: '0.375rem', padding: '0.75rem', minWidth: '80px', flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#1e293b', marginBottom: '0.5rem' }}>
                  {formatDay(day.date)}
                </div>
                <div style={{ fontSize: '1.5rem', color: '#3b82f6', marginBottom: '0.5rem' }}>
                  {getWeatherIcon(day.condition)}
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', marginBottom: '0.25rem' }}>
                  {formatTemperature(day.high)} / {formatTemperature(day.low)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center', marginBottom: '0.25rem' }}>
                  {day.condition}
                </div>
                {day.precipitation > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6' }}>
                    {formatNumber(day.precipitation, 0, '%')} ☔
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default WeatherWidget;